/**
 * HTTP provider — the live façade client.
 *
 * Contract facts this implements, all straight out of `openapi/openapi.yaml`:
 *  - `baseUrl` is the integration prefix (…/integration/v1). The `/ops/v1` paths
 *    are root-absolute — the document pins them with `servers: [{url: /}]` — so
 *    they are resolved against `opsBaseUrl` (derived from `baseUrl` by default,
 *    overridable) instead of being appended to the integration prefix.
 *  - `Authorization: Bearer <JWT>` comes from a token provider. The console NEVER
 *    holds a signing key: the bearer is minted server-side, and this client only
 *    forwards whatever the provider hands it. For the console that minting door is
 *    the human session path — `POST /ops/v1/sessions` (R1, landed with U-08-f);
 *    ruling D1 of `docs/plans/2026-10-16-u08-route-capability-and-console-auth.md`
 *    decided there is NO proxy or BFF in front of these doors and none is planned.
 *  - Writes send `Idempotency-Key` (optional per contract, always sent here; the
 *    v1.1 additive `/ops/v1` writes REQUIRE it — missing → 422).
 *  - Errors are `application/problem+json`; 429 additionally carries
 *    `Retry-After`, which the problem renderer shows.
 */

import {
  parseActionResponse,
  parseActivationAccepted,
  parseActivationStatus,
  parseAlarmAckResponse,
  parseAlarmAssignResponse,
  parseMaintenanceCancelResponse,
  parseMaintenanceCreateResponse,
  parseIncident,
  parseIncidentAttachResponse,
  parseIncidentCorrelate,
  parseIncidentList,
  parseNotificationChannel,
  parseNotificationChannelDeleted,
  parseNotificationChannelList,
  parseNotificationDelivery,
  parseNotificationDeliveryList,
  parseMaintenanceList,
  parseAlarmClearResponse,
  parseAlarmEscalateResponse,
  parseAlarms,
  parseAlarmStats,
  parseAuditList,
  parseBindingResponse,
  parseCampaign,
  parseCampaignAccepted,
  parseCampaignHalted,
  parseCampaignList,
  parseCampaignRolledBack,
  parseCampaignScheduled,
  parseDeletedResponse,
  parseDevicesList,
  parseDeviceStats,
  parseDeviceSummary,
  parseEffectivePermissions,
  parseEvents,
  parseFirmwareList,
  parseFirmwareUploadAccepted,
  parseHealth,
  parseJob,
  parseJobsList,
  parseOperationRunAccepted,
  parseOperationValue,
  parseOperationWriteAccepted,
  parsePresetWrite,
  parseReconciliationList,
  parseTemplateList,
  parseOperationsResponse,
  parseOnuAuthorizeResponse,
  parseOnuTable,
  parseOltList,
  parsePonCapacity,
  parseOpticalHistory,
  parseOpticalDegrading,
  parseOpticalWorst,
  parseOpticalRead,
  parseParams,
  parseProblem,
  parseProfileActivationAccepted,
  parseProfileRead,
  parseProfileReadiness,
  parseProfileValidationReport,
  parseProfileVersionList,
  parseSessionGranted,
  parseSessionRevoked,
  parseServiceProfile,
  parseServiceProfileList,
  parseSigningKeyList,
  parseStations,
  parseSubscriber,
  parseSubscriberList,
  parseSubscriberOverview,
  parseSubscriberSearchResponse,
  parseOpsSettingsRead,
  parseUserList,
  parseUserMutationAccepted,
  parseMatrixMutationAccepted,
  parsePermissionMatrix,
  parseSystemComponents,
  parseSystemQueues,
  parseSystemWorkers,
} from './conform';
import {
  type ReadOptions,
  assertWriteKey,
  type ConsoleApi,
  type FirmwareImageUpload,
  type WriteOptions,
} from './consoleApi';
import {
  ApiError,
  looksLikeProblem,
  parseRetryAfter,
  pendingEndpoint,
  synthesizeProblem,
} from './errors';
import { openEventStream } from './sse';
import type {
  ActionRequest,
  ActivationRequest,
  AlarmAckRequest,
  AlarmAssignRequest,
  AlarmClearRequest,
  AlarmEscalateRequest,
  BindingRequest,
  CampaignCreateRequest,
  CampaignRollbackRequest,
  CloudEvent,
  DiagnosticsRequest,
  ListAlarmsQuery,
  ListAuditQuery,
  ListCampaignsQuery,
  ListDevicesQuery,
  ListEventsQuery,
  ListFirmwareQuery,
  ListJobsQuery,
  ListMaintenanceQuery,
  ListOltOnusQuery,
  ListOperationsQuery,
  IncidentAttachRequest,
  IncidentCloseRequest,
  IncidentCreateRequest,
  IncidentNoteRequest,
  ListIncidentCorrelateQuery,
  ListIncidentsQuery,
  ListNotificationDeliveriesQuery,
  NotificationChannelCreateRequest,
  NotificationChannelUpdateRequest,
  MaintenanceCreateRequest,
  ListParamsQuery,
  ListServiceProfilesQuery,
  ListStationsQuery,
  ListSubscribersQuery,
  OnuAuthorizeRequest,
  OperationRunRequest,
  OperationWriteRequest,
  OpticalDegradingWindow,
  OpticalWindow,
  RawWriteRequest,
  ServiceProfileWriteRequest,
  SubscriberCreateRequest,
  SubscriberSearchRequest,
} from './types';

/** Supplies a bearer assertion for one request (may cache/refresh internally). */
export type TokenProvider = () => Promise<string>;

export interface HttpProviderOptions {
  /** `https://acs.example/integration/v1`, or a same-origin prefix like `/api`. */
  baseUrl: string;
  token: TokenProvider;
  /**
   * Root for the `/ops/v1` surface. Defaults to `opsBaseUrlFor(baseUrl)`; set it only
   * when the two surfaces genuinely do not share the root that derivation assumes.
   * This is a plain base-URL override, not a BFF mount: under ruling D1 the console
   * authenticates through the human session path and no proxy or BFF exists or is
   * planned, and `app/provider.ts` never sets this option.
   */
  opsBaseUrl?: string;
  /** Injectable for tests; defaults to the platform `fetch`. */
  fetchImpl?: typeof fetch;
  requestTimeoutMs?: number;
  /** Label used in transport-error messages. */
  label?: string;
  /**
   * Called when a door answered **401** to a request that carried this console's bearer token.
   *
   * That status is the only thing that makes a revocation visible in a browser: the JWT is
   * unforgeable, so a session the server revoked stays syntactically valid until it expires, and the
   * session RECORD is what refuses it (`acs_service/routers/identity.py` §۵). Without this hook the
   * console would keep a dead token and keep showing the operator a healthy-looking page.
   * `api/session.ts` is the intended owner; it is not called for the login door, which carries no
   * bearer and answers 401 for a wrong password.
   */
  onUnauthorized?: (status: number) => void;
}

type QueryValue = string | number | boolean | undefined | null;

const DEFAULT_TIMEOUT_MS = 15_000;

/**
 * A firmware image is a transfer, not a conversation: the ceiling the door enforces is a
 * deployment number (`ACS_FIRMWARE_MAX_BYTES`, default 128 MiB), and a 100 MB upload over
 * an access link legitimately takes minutes. Five minutes bounds the request without
 * putting the 15 s read timeout on the one call it does not fit.
 */
export const IMAGE_UPLOAD_TIMEOUT_MS = 300_000;

export function createHttpProvider(options: HttpProviderOptions): ConsoleApi {
  const { baseUrl } = options;
  const opsBase = options.opsBaseUrl ?? opsBaseUrlFor(baseUrl);
  const token = options.token;
  const doFetch = options.fetchImpl ?? globalThis.fetch;
  const timeoutMs = options.requestTimeoutMs ?? DEFAULT_TIMEOUT_MS;
  const label = options.label ?? 'ACS façade';

  if (typeof doFetch !== 'function') {
    throw new ApiError('contract', 'createHttpProvider: no fetch implementation');
  }

  async function request<T>(
    path: string,
    init: {
      method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
      query?: Record<string, QueryValue>;
      body?: unknown;
      /**
       * The E1 image channel: the body IS the file (`application/octet-stream`), with its
       * metadata in the query. Kept separate from `body` so no JSON path can ever stringify
       * an image into a string.
       */
      rawBody?: Blob | Uint8Array;
      idempotencyKey?: string;
      /** Root to resolve `path` against; defaults to the integration prefix. */
      base?: string;
      /**
       * The login door: it authenticates itself, so it carries neither a bearer nor a key.
       * Skipping `token()` here is what lets a signed-out browser call it at all, and skipping the
       * 401 notification is what stops a wrong password from firing a "you were signed out" event
       * when nothing was ever signed in.
       */
      anonymous?: boolean;
      /**
       * Per-request timeout. The default is a *conversation* bound (15 s); an image upload
       * is a transfer, and a fleet-sized one is legitimately minutes long — aborting it at
       * 15 s would make the large-image channel unusable from the browser, which is the
       * one thing G-FW-01 was about.
       */
      timeoutMs?: number;
      /**
       * DA-63: the caller's cancellation (react-query hands a `QueryFunctionContext.signal`).
       * It is COMBINED with the budget controller — one abort listener forwards the external
       * cancellation onto the internal controller, so exactly one signal reaches `fetch` and
       * the two guards (caller gives up, budget expires) stay one mechanism. An external
       * cancellation is reported as its own fact («was cancelled»), distinct from the budget's
       * «did not answer within».
       */
      signal?: AbortSignal;
      parse: (value: unknown) => T;
    },
  ): Promise<T> {
    const url = buildUrl(init.base ?? baseUrl, path, init.query);
    const bearer = init.anonymous === true ? null : await token();
    const budget = init.timeoutMs ?? timeoutMs;

    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), budget);
    const external = init.signal;
    const forwardExternalAbort = () => controller.abort();
    if (external !== undefined) {
      if (external.aborted) controller.abort();
      else external.addEventListener('abort', forwardExternalAbort, { once: true });
    }
    let response: Response;
    let text: string;
    try {
      response = await doFetch(url, {
        method: init.method,
        signal: controller.signal,
        headers: {
          Accept: 'application/json',
          ...(bearer === null ? {} : { Authorization: `Bearer ${bearer}` }),
          ...(init.body === undefined && init.rawBody === undefined
            ? {}
            : {
                'Content-Type':
                  init.rawBody === undefined ? 'application/json' : 'application/octet-stream',
              }),
          ...(init.idempotencyKey === undefined ? {} : { 'Idempotency-Key': init.idempotencyKey }),
        },
        ...(init.rawBody !== undefined
          ? {
              // `Uint8Array<ArrayBufferLike>` is a valid fetch body; this lib's `BodyInit` names
              // the narrower `ArrayBufferView<ArrayBuffer>`. The bytes are owned by the caller and
              // passed through untouched, which is the whole point of the raw channel.
              body: init.rawBody as BodyInit,
            }
          : init.body === undefined
            ? {}
            : { body: JSON.stringify(init.body) }),
      });
      // Inside the guarded block, so the budget covers the WHOLE response and an abort
      // mid-body is mapped by the catch above (DA-62) instead of escaping raw.
      text = await response.text();
    } catch (cause) {
      const aborted = cause instanceof Error && cause.name === 'AbortError';
      throw new ApiError(
        'transport',
        aborted && external?.aborted === true
          ? `${label} was cancelled before it finished`
          : aborted
            ? `${label} did not answer within ${budget} ms`
            : `${label} is unreachable: ${String(cause)}`,
        { cause: cause as object },
      );
    } finally {
      clearTimeout(timer);
      if (external !== undefined) external.removeEventListener('abort', forwardExternalAbort);
    }
    let payload: unknown;
    let parsed = false;
    if (text.length > 0) {
      try {
        payload = JSON.parse(text);
        parsed = true;
      } catch {
        parsed = false;
      }
    }

    if (!response.ok) {
      const retryAfter = parseRetryAfter(response.headers.get('Retry-After'));
      // A 401 against a request that carried our bearer means the session behind it is gone —
      // revoked, expired, disabled, or its user deleted. Tell the session store BEFORE the throw,
      // so a component that renders the error is already looking at a signed-out console.
      if (response.status === 401 && bearer !== null) options.onUnauthorized?.(response.status);
      if (parsed && looksLikeProblem(payload)) {
        throw new ApiError('problem', `problem+json ${response.status}`, {
          problem: parseProblem(payload),
          status: response.status,
          ...(retryAfter === undefined ? {} : { retryAfterSeconds: retryAfter }),
        });
      }
      // Non-conforming error body: stay honest about what actually arrived.
      throw new ApiError(
        'contract',
        `${label} answered ${response.status} without a problem+json body`,
        {
          problem: synthesizeProblem(
            response.status,
            text.slice(0, 400) || `${label} returned an empty error body`,
          ),
          status: response.status,
          ...(retryAfter === undefined ? {} : { retryAfterSeconds: retryAfter }),
          rawBody: text.slice(0, 400),
        },
      );
    }

    if (!parsed) {
      throw new ApiError('contract', `${label} answered ${response.status} with a non-JSON body`, {
        status: response.status,
        rawBody: text.slice(0, 200),
      });
    }

    return init.parse(payload);
  }

  return {
    dataSource: 'http',

    health: (options?: ReadOptions) =>
      request('/health', { method: 'GET', signal: options?.signal, parse: parseHealth }),

    listDevices: (query: ListDevicesQuery, options?: ReadOptions) =>
      request('/devices', {
        method: 'GET',
        signal: options?.signal,
        query: toQuery(query),
        parse: parseDevicesList,
      }),

    getDevice: async (acsDeviceId: string, options?: ReadOptions) =>
      request(`/devices/${encodeId(acsDeviceId)}`, {
        method: 'GET',
        signal: options?.signal,
        parse: parseDeviceSummary,
      }),

    /* The dashboard's two aggregate reads (V4 / G-TLM-05). They sit on the `/ops/v1` root, and
       they answer over the WHOLE store — which is why the cards read them instead of reducing
       `GET /devices`, whose answer is one page. Nothing here samples, pages or re-counts. */
    getDeviceStats: (options?: ReadOptions) =>
      request('/stats/devices', {
        method: 'GET',
        signal: options?.signal,
        base: opsBase,
        parse: parseDeviceStats,
      }),

    getAlarmStats: (options?: ReadOptions) =>
      request('/stats/alarms', {
        method: 'GET',
        signal: options?.signal,
        base: opsBase,
        parse: parseAlarmStats,
      }),

    getParameters: async (acsDeviceId: string, query: ListParamsQuery, options?: ReadOptions) =>
      request(`/devices/${encodeId(acsDeviceId)}/parameters`, {
        method: 'GET',
        signal: options?.signal,
        query: toQuery(query),
        parse: parseParams,
      }),

    getStations: async (acsDeviceId: string, query: ListStationsQuery, options?: ReadOptions) =>
      request(`/devices/${encodeId(acsDeviceId)}/stations`, {
        method: 'GET',
        signal: options?.signal,
        query: toQuery(query),
        parse: parseStations,
      }),

    getJob: (jobId: string) =>
      request(`/jobs/${encodeId(jobId)}`, { method: 'GET', parse: parseJob }),

    pollEvents: (query: ListEventsQuery, options?: ReadOptions) =>
      request('/events', {
        method: 'GET',
        signal: options?.signal,
        query: toQuery(query),
        parse: parseEvents,
      }),

    /* ---- the live-event channel — `GET /events/stream` (N9 slice 2, G-EVT-05/G-UI-01) ----
       Not a `request()` call: the body never ends (until the door's bounded lifetime), the
       frames are SSE-framed text, and the bearer must ride the one long-lived request — the
       reason a browser `EventSource` cannot serve this console. `api/sse.ts` owns the
       framing and the reconnect loop; this is only the wiring to THIS provider's base URL,
       token and 401 hook. A frame whose `data` is not a CloudEvent JSON is skipped, not
       crashed on: a heartbeat never carries data, and one malformed block must not kill a
       feed the operator is watching. */
    streamEvents: (handler, streamOptions) => {
      return openEventStream({
        url: buildUrl(baseUrl, '/events/stream'),
        token,
        onEvent: (frame) => {
          try {
            const event = JSON.parse(frame.data) as CloudEvent;
            handler.onEvent({ cursorId: frame.id ?? null, event });
          } catch {
            /* a non-JSON block (or a broken frame) is skipped; the stream continues */
          }
        },
        onStatus: handler.onStatus,
        signal: streamOptions.signal,
        onUnauthorized: (status) => {
          // Same rule as every other door: a 401 against OUR bearer means the session
          // behind it is gone — tell the session store (the provider-level hook) and the
          // channel's caller before anything else reacts.
          options.onUnauthorized?.(status);
          streamOptions.onUnauthorized?.(status);
        },
      });
    },

    runAction: async (acsDeviceId: string, body: ActionRequest, opts: WriteOptions) =>
      request(`/devices/${encodeId(acsDeviceId)}/actions`, {
        method: 'POST',
        body,
        idempotencyKey: assertWriteKey(opts.idempotencyKey),
        parse: parseActionResponse,
      }),

    bindDevice: async (body: BindingRequest, opts: WriteOptions) =>
      request('/devices:bind', {
        method: 'POST',
        body,
        idempotencyKey: assertWriteKey(opts.idempotencyKey),
        parse: parseBindingResponse,
      }),

    createActivation: async (body: ActivationRequest, opts: WriteOptions) =>
      request('/activations', {
        method: 'POST',
        base: opsBase,
        body,
        idempotencyKey: assertWriteKey(opts.idempotencyKey),
        parse: parseActivationAccepted,
      }),

    getActivation: async (activationId: string, options?: ReadOptions) =>
      request(`/activations/${encodeId(activationId)}`, {
        method: 'GET',
        signal: options?.signal,
        base: opsBase,
        parse: parseActivationStatus,
      }),

    runDiagnostics: async (acsDeviceId: string, body: DiagnosticsRequest, opts: WriteOptions) =>
      request(`/devices/${encodeId(acsDeviceId)}/diagnostics`, {
        method: 'POST',
        body,
        idempotencyKey: assertWriteKey(opts.idempotencyKey),
        parse: parseActionResponse,
      }),

    getDiagnostics: async (acsDeviceId: string, jobId: string, options?: ReadOptions) =>
      request(`/devices/${encodeId(acsDeviceId)}/diagnostics/${encodeId(jobId)}`, {
        method: 'GET',
        signal: options?.signal,
        parse: parseJob,
      }),

    /* ---- alarms feed — `/ops/v1/alarms` (scope alarm:read) ---- */

    listAlarms: (query: ListAlarmsQuery, options?: ReadOptions) =>
      request('/alarms', {
        method: 'GET',
        signal: options?.signal,
        base: opsBase,
        query: {
          severity: query.severity,
          device: query.device,
          // `acked=false` must serialise as `false`, not be dropped.
          acked: typeof query.acked === 'boolean' ? query.acked : undefined,
          cursor: query.cursor,
          limit: query.limit,
        },
        parse: parseAlarms,
      }),

    ackAlarm: async (eventId: string, body: AlarmAckRequest, opts: WriteOptions) =>
      request(`/alarms/${encodeId(eventId)}:ack`, {
        method: 'POST',
        base: opsBase,
        body,
        idempotencyKey: assertWriteKey(opts.idempotencyKey),
        parse: parseAlarmAckResponse,
      }),

    /* ---- the lifecycle doors (N2) — `:clear`, `:escalate`, `:assign` ----
       The server's replay guard is the row's own state, so these doors need
       no Idempotency-Key to be safe; the console still sends one, derived
       from the alarm id, so a resend of the same intent cannot mint a
       second key. */

    clearAlarm: async (eventId: string, body: AlarmClearRequest, opts: WriteOptions) =>
      request(`/alarms/${encodeId(eventId)}:clear`, {
        method: 'POST',
        base: opsBase,
        body,
        idempotencyKey: assertWriteKey(opts.idempotencyKey),
        parse: parseAlarmClearResponse,
      }),

    escalateAlarm: async (eventId: string, body: AlarmEscalateRequest, opts: WriteOptions) =>
      request(`/alarms/${encodeId(eventId)}:escalate`, {
        method: 'POST',
        base: opsBase,
        body,
        idempotencyKey: assertWriteKey(opts.idempotencyKey),
        parse: parseAlarmEscalateResponse,
      }),

    assignAlarm: async (eventId: string, body: AlarmAssignRequest, opts: WriteOptions) =>
      request(`/alarms/${encodeId(eventId)}:assign`, {
        method: 'POST',
        base: opsBase,
        body,
        idempotencyKey: assertWriteKey(opts.idempotencyKey),
        parse: parseAlarmAssignResponse,
      }),

    /* ---- maintenance windows (ALM-05) — `/ops/v1/maintenance` ----
       The list is OFFSET-paged (the door's own shape, not a cursor); both
       writes are human-only at `alarm.ack` edit and carry the console's
       always-a-key policy. */

    listMaintenance: (query: ListMaintenanceQuery, options?: ReadOptions) =>
      request('/maintenance', {
        method: 'GET',
        signal: options?.signal,
        base: opsBase,
        query: {
          // An empty state means «all» and must not serialise as `state=`.
          state: query.state || undefined,
          offset: query.offset,
          limit: query.limit,
        },
        parse: parseMaintenanceList,
      }),

    createMaintenance: async (body: MaintenanceCreateRequest, opts: WriteOptions) =>
      request('/maintenance', {
        method: 'POST',
        base: opsBase,
        body,
        idempotencyKey: assertWriteKey(opts.idempotencyKey),
        parse: parseMaintenanceCreateResponse,
      }),

    cancelMaintenance: async (windowId: string, opts: WriteOptions) =>
      request(`/maintenance/${encodeId(windowId)}:cancel`, {
        method: 'POST',
        base: opsBase,
        // The door reads no body; the key IS the request's identity.
        body: {},
        idempotencyKey: assertWriteKey(opts.idempotencyKey),
        parse: parseMaintenanceCancelResponse,
      }),

    /* ---- incidents (N5) — `/ops/v1/incidents` ----
       The correlate door is a READ with a query floor; the two lifecycle
       writes that need a key are create (a replay would be a second
       incident) and note (a replay would be a second timeline entry);
       attach and close ride their natural replay. */

    correlateIncidents: (
      query: ListIncidentCorrelateQuery & { offset?: number },
      options?: ReadOptions,
    ) =>
      request('/incidents:correlate', {
        method: 'GET',
        signal: options?.signal,
        base: opsBase,
        query: { min_devices: query.min_devices },
        parse: parseIncidentCorrelate,
      }),

    listIncidents: (query: ListIncidentsQuery, options?: ReadOptions) =>
      request('/incidents', {
        method: 'GET',
        signal: options?.signal,
        base: opsBase,
        query: {
          state: query.state || undefined,
          offset: query.offset,
          limit: query.limit,
        },
        parse: parseIncidentList,
      }),

    getIncident: (incidentId: string, options?: ReadOptions) =>
      request(`/incidents/${encodeId(incidentId)}`, {
        method: 'GET',
        signal: options?.signal,
        base: opsBase,
        parse: parseIncident,
      }),

    createIncident: async (body: IncidentCreateRequest, opts: WriteOptions) =>
      request('/incidents', {
        method: 'POST',
        base: opsBase,
        body,
        idempotencyKey: assertWriteKey(opts.idempotencyKey),
        parse: parseIncident,
      }),

    noteIncident: async (incidentId: string, body: IncidentNoteRequest, opts: WriteOptions) =>
      request(`/incidents/${encodeId(incidentId)}:note`, {
        method: 'POST',
        base: opsBase,
        body,
        idempotencyKey: assertWriteKey(opts.idempotencyKey),
        parse: parseIncident,
      }),

    attachIncident: async (incidentId: string, body: IncidentAttachRequest, opts: WriteOptions) =>
      request(`/incidents/${encodeId(incidentId)}:attach`, {
        method: 'POST',
        base: opsBase,
        body,
        idempotencyKey: assertWriteKey(opts.idempotencyKey),
        parse: parseIncidentAttachResponse,
      }),

    closeIncident: async (incidentId: string, body: IncidentCloseRequest, opts: WriteOptions) =>
      request(`/incidents/${encodeId(incidentId)}:close`, {
        method: 'POST',
        base: opsBase,
        body,
        idempotencyKey: assertWriteKey(opts.idempotencyKey),
        parse: parseIncident,
      }),

    /* ---- notification channels (N6) — `/ops/v1/notification-channels` ----
       ALL six doors answer to ONE key — `notify.manage`, reads included
       (plan D8: the URL may carry a token, so the read is not free). Every
       write takes a key; `:test` mints a REAL pending intent and its key IS
       the intent's dedupe key (202 with the flat delivery). */

    listNotificationChannels: (options?: ReadOptions) =>
      request('/notification-channels', {
        method: 'GET',
        signal: options?.signal,
        base: opsBase,
        parse: parseNotificationChannelList,
      }),

    createNotificationChannel: async (body: NotificationChannelCreateRequest, opts: WriteOptions) =>
      request('/notification-channels', {
        method: 'POST',
        base: opsBase,
        body,
        idempotencyKey: assertWriteKey(opts.idempotencyKey),
        parse: parseNotificationChannel,
      }),

    updateNotificationChannel: async (
      channelId: string,
      body: NotificationChannelUpdateRequest,
      opts: WriteOptions,
    ) =>
      request(`/notification-channels/${encodeId(channelId)}`, {
        method: 'PUT',
        base: opsBase,
        body,
        idempotencyKey: assertWriteKey(opts.idempotencyKey),
        parse: parseNotificationChannel,
      }),

    deleteNotificationChannel: async (channelId: string, opts: WriteOptions) =>
      request(`/notification-channels/${encodeId(channelId)}`, {
        method: 'DELETE',
        base: opsBase,
        // The door reads no body; the key IS the request's identity.
        body: {},
        idempotencyKey: assertWriteKey(opts.idempotencyKey),
        parse: parseNotificationChannelDeleted,
      }),

    testNotificationChannel: async (channelId: string, opts: WriteOptions) =>
      request(`/notification-channels/${encodeId(channelId)}:test`, {
        method: 'POST',
        base: opsBase,
        // The door reads no body; the key IS the intent's dedupe key.
        body: {},
        idempotencyKey: assertWriteKey(opts.idempotencyKey),
        parse: parseNotificationDelivery,
      }),

    listNotificationDeliveries: (query: ListNotificationDeliveriesQuery, options?: ReadOptions) =>
      request('/notification-deliveries', {
        method: 'GET',
        signal: options?.signal,
        base: opsBase,
        query: {
          channel_id: query.channel_id || undefined,
          limit: query.limit,
        },
        parse: parseNotificationDeliveryList,
      }),

    /* ---- audit ledger — `GET /ops/v1/audit` (R7, U-16-d) ---- */

    /**
     * The door's two filters are EXACT matches on the stored top-level keys and an empty value means
     * «all» (`routers/audit.py`), so nothing here trims or normalises them beyond the page's own
     * `trim()`; a filter that matches nothing answers an empty page with the note, never a 404.
     */
    listAudit: (query: ListAuditQuery, options?: ReadOptions) =>
      request('/audit', {
        method: 'GET',
        signal: options?.signal,
        base: opsBase,
        query: {
          kind: query.kind,
          device_id: query.device_id,
          cursor: query.cursor,
          limit: query.limit,
        },
        parse: parseAuditList,
      }),

    /* ---- signing keys — `GET /ops/v1/keys` (door U-08-b, page U-16-e1) ---- */

    /**
     * No query parameters, because the door declares none: `acs_service/routers/keys.py` takes a
     * request and a capability and reads `jwks.inventory()`. A `limit` or `cursor` sent here would be
     * an invented filter (the plan's D5 / G-PRV-01 rule cuts both ways), and the published JWKS is the
     * whole answer — this door is not paginated and never will be from the client side.
     */
    listKeys: (options?: ReadOptions) =>
      request('/keys', {
        method: 'GET',
        signal: options?.signal,
        base: opsBase,
        parse: parseSigningKeyList,
      }),

    /* ---- operator profile — the six doors of `routers/profile.py` (U-06, page U-16-e2) ---- */

    /** No query parameters: the door declares none, and inventing a filter is D5 in both directions. */
    getOperatorProfile: (options?: ReadOptions) =>
      request('/operator-profile', {
        method: 'GET',
        signal: options?.signal,
        base: opsBase,
        parse: parseProfileRead,
      }),

    /**
     * `POST /ops/v1/operator-profile:validate` carries NO `Idempotency-Key` on purpose: the door has
     * no effect, replay is trivially equivalent, and a stored replay would return the first verdict
     * for a document the operator edited in between (`routers/profile.py`'s own sentence). The body is
     * the open candidate: the report answers EVERY complaint at once, which is why the console never
     * pre-judges the document with its own schema before sending.
     */
    validateOperatorProfile: (document) =>
      request('/operator-profile:validate', {
        method: 'POST',
        base: opsBase,
        body: document,
        parse: parseProfileValidationReport,
      }),

    /** The header is REQUIRED here: every success mints a version, so a retry without a key is a second version nobody approved twice. */
    activateOperatorProfile: async (body, opts) =>
      request('/operator-profile', {
        method: 'PUT',
        base: opsBase,
        body,
        idempotencyKey: assertWriteKey(opts.idempotencyKey),
        parse: parseProfileActivationAccepted,
      }),

    listOperatorProfileVersions: (options?: ReadOptions) =>
      request('/operator-profile/versions', {
        method: 'GET',
        signal: options?.signal,
        base: opsBase,
        parse: parseProfileVersionList,
      }),

    /** Forward rollback: a NEW version, never a rewrite; the key is required for the same reason as the PUT. */
    rollbackOperatorProfile: async (body, opts) =>
      request('/operator-profile:rollback', {
        method: 'POST',
        base: opsBase,
        body,
        idempotencyKey: assertWriteKey(opts.idempotencyKey),
        parse: parseProfileActivationAccepted,
      }),

    getOperatorProfileReadiness: (options?: ReadOptions) =>
      request('/operator-profile/readiness', {
        method: 'GET',
        signal: options?.signal,
        base: opsBase,
        parse: parseProfileReadiness,
      }),

    /* ---- OLT views + optical — root-absolute `/ops/v1` (scope ops:read) ---- */

    listOlts: (options?: ReadOptions) =>
      request('/olts', {
        method: 'GET',
        signal: options?.signal,
        base: opsBase,
        parse: parseOltList,
      }),

    listOltOnus: (oltId: string, query: ListOltOnusQuery, options?: ReadOptions) =>
      request(`/olts/${encodeId(oltId)}/onus`, {
        method: 'GET',
        signal: options?.signal,
        base: opsBase,
        query: { status: query.status },
        parse: parseOnuTable,
      }),

    getOltPonCapacity: (oltId: string, options?: ReadOptions) =>
      request(`/olts/${encodeId(oltId)}/pon-capacity`, {
        method: 'GET',
        signal: options?.signal,
        base: opsBase,
        parse: parsePonCapacity,
      }),

    authorizeOnu: async (oltId: string, body: OnuAuthorizeRequest, opts: WriteOptions) =>
      request(`/olts/${encodeId(oltId)}/onus:authorize`, {
        method: 'POST',
        base: opsBase,
        body,
        idempotencyKey: assertWriteKey(opts.idempotencyKey),
        parse: parseOnuAuthorizeResponse,
      }),

    getDeviceOptical: (
      acsDeviceId: string,
      query: { window?: OpticalWindow },
      options?: ReadOptions,
    ) =>
      request(`/devices/${encodeId(acsDeviceId)}/optical`, {
        method: 'GET',
        signal: options?.signal,
        base: opsBase,
        query: { window: query.window },
        parse: parseOpticalRead,
      }),

    getDeviceOpticalHistory: (
      acsDeviceId: string,
      query: { window?: OpticalWindow },
      options?: ReadOptions,
    ) =>
      request(`/devices/${encodeId(acsDeviceId)}/optical/history`, {
        method: 'GET',
        signal: options?.signal,
        base: opsBase,
        query: { window: query.window },
        parse: parseOpticalHistory,
      }),

    getOpticalWorst: (query: { window?: OpticalWindow; limit?: number }, options?: ReadOptions) =>
      request('/optical/worst', {
        method: 'GET',
        signal: options?.signal,
        base: opsBase,
        query: { window: query.window, limit: query.limit },
        parse: parseOpticalWorst,
      }),

    getOpticalDegrading: (
      query: { window?: OpticalDegradingWindow; limit?: number },
      options?: ReadOptions,
    ) =>
      request('/optical/degrading', {
        method: 'GET',
        signal: options?.signal,
        base: opsBase,
        query: { window: query.window, limit: query.limit },
        parse: parseOpticalDegrading,
      }),

    /* ---- firmware registry + campaigns — root-absolute `/ops/v1` ---- */

    listFirmware: (query: ListFirmwareQuery, options?: ReadOptions) =>
      request('/firmware', {
        method: 'GET',
        signal: options?.signal,
        base: opsBase,
        query: {
          brand: query.brand,
          model: query.model,
          cursor: query.cursor,
          limit: query.limit,
        },
        parse: parseFirmwareList,
      }),

    listCampaigns: (query: ListCampaignsQuery, options?: ReadOptions) =>
      request('/campaigns', {
        method: 'GET',
        signal: options?.signal,
        base: opsBase,
        query: { status: query.status, cursor: query.cursor, limit: query.limit },
        parse: parseCampaignList,
      }),

    getCampaign: (campaignId: string, options?: ReadOptions) =>
      request(`/campaigns/${encodeId(campaignId)}`, {
        method: 'GET',
        signal: options?.signal,
        base: opsBase,
        parse: parseCampaign,
      }),

    uploadFirmwareImage: async (image: FirmwareImageUpload, opts: WriteOptions) =>
      request('/firmware:upload', {
        method: 'POST',
        base: opsBase,
        // the metadata travels as query parameters because the body is the image
        query: {
          brand: image.brand,
          model: image.model,
          version: image.version,
          checksum_sha256: image.checksum_sha256,
          size_bytes: image.size_bytes,
        },
        rawBody: image.body,
        idempotencyKey: assertWriteKey(opts.idempotencyKey),
        timeoutMs: IMAGE_UPLOAD_TIMEOUT_MS,
        parse: parseFirmwareUploadAccepted,
      }),

    deleteFirmware: async (fileId: string, opts: WriteOptions) =>
      request(`/firmware/${encodeId(fileId)}`, {
        method: 'DELETE',
        base: opsBase,
        idempotencyKey: assertWriteKey(opts.idempotencyKey),
        parse: parseDeletedResponse,
      }),

    createCampaign: async (body: CampaignCreateRequest, opts: WriteOptions) =>
      request('/campaigns', {
        method: 'POST',
        base: opsBase,
        body,
        idempotencyKey: assertWriteKey(opts.idempotencyKey),
        parse: parseCampaignScheduled,
      }),

    haltCampaign: async (campaignId: string, opts: WriteOptions) =>
      request(`/campaigns/${encodeId(campaignId)}:halt`, {
        method: 'POST',
        base: opsBase,
        idempotencyKey: assertWriteKey(opts.idempotencyKey),
        parse: parseCampaignHalted,
      }),

    resumeCampaign: async (campaignId: string, opts: WriteOptions) =>
      request(`/campaigns/${encodeId(campaignId)}:resume`, {
        method: 'POST',
        base: opsBase,
        idempotencyKey: assertWriteKey(opts.idempotencyKey),
        parse: parseCampaignAccepted,
      }),

    rollbackCampaign: async (
      campaignId: string,
      body: CampaignRollbackRequest,
      opts: WriteOptions,
    ) =>
      request(`/campaigns/${encodeId(campaignId)}:rollback`, {
        method: 'POST',
        base: opsBase,
        body,
        idempotencyKey: assertWriteKey(opts.idempotencyKey),
        parse: parseCampaignRolledBack,
      }),

    /* ---- fleet jobs — `GET /ops/v1/jobs` (`routers/system.py:177`, capability `jobs.read`) ----
       One door, two shapes. `type` is sent as the door names it and the door reads it as an ACTION
       PREFIX, not an enum (`diagnostics:` → the probe family, `activation` → ZTP runs); the console
       never re-derives an action family in the browser. `total` is the count matching this filter
       over the whole set, and the row is a lifecycle only — the claim about the device (`verdict`)
       arrives on the full job record, so a 202 accepted here is never rendered as applied (D6). */

    listJobs: (query: ListJobsQuery, options?: ReadOptions) =>
      request('/jobs', {
        method: 'GET',
        signal: options?.signal,
        base: opsBase,
        query: {
          device: query.device?.trim() || undefined,
          status: query.status?.trim() || undefined,
          type: query.type?.trim() || undefined,
          cursor: query.cursor,
          limit: query.limit,
        },
        parse: parseJobsList,
      }),

    listDeviceJobs: (acsDeviceId, query = {}, options?: ReadOptions) =>
      request('/jobs', {
        method: 'GET',
        signal: options?.signal,
        base: opsBase,
        query: {
          device: acsDeviceId,
          status: query.status?.trim() || undefined,
          type: query.type?.trim() || undefined,
          cursor: query.cursor,
          limit: query.limit,
        },
        parse: parseJobsList,
      }),

    /* ---- per-device operations / bulk / workflow — `routers/operations.py` (U-16-f) ----
       Reads take `device.params.read` and no `Idempotency-Key` (the module's own sentence: a read
       that changes nothing has nothing to replay). The three writes take `Idempotency-Key` as a
       REQUIRED header (missing → 422) and answer **202 queued** — an acceptance whose only claim is
       a `job_id`. `PUT …/parameters/raw` sits behind `device.params.raw`, a third key, because the
       matrix gives it to owner/admin only and a slot write must not reach it (§۶.۱ layer ۱). */

    listDeviceOperations: (
      acsDeviceId: string,
      query: ListOperationsQuery = {},
      options?: ReadOptions,
    ) =>
      request(`/devices/${encodeId(acsDeviceId)}/operations`, {
        method: 'GET',
        signal: options?.signal,
        base: opsBase,
        query: {
          root: query.root,
          available: typeof query.available === 'boolean' ? query.available : undefined,
          cursor: query.cursor,
          limit: query.limit,
        },
        parse: parseOperationsResponse,
      }),

    getOperationValue: (
      acsDeviceId: string,
      op: string,
      query: { refresh?: boolean } = {},
      options?: ReadOptions,
    ) =>
      request(`/devices/${encodeId(acsDeviceId)}/operations/${encodeId(op)}/value`, {
        method: 'GET',
        signal: options?.signal,
        base: opsBase,
        query: { refresh: query.refresh },
        parse: parseOperationValue,
      }),

    setOperationValue: async (
      acsDeviceId: string,
      op: string,
      body: OperationWriteRequest,
      opts: WriteOptions,
    ) =>
      request(`/devices/${encodeId(acsDeviceId)}/operations/${encodeId(op)}`, {
        method: 'PUT',
        base: opsBase,
        body,
        idempotencyKey: assertWriteKey(opts.idempotencyKey),
        parse: parseOperationWriteAccepted,
      }),

    runOperation: async (
      acsDeviceId: string,
      op: string,
      body: OperationRunRequest,
      opts: WriteOptions,
    ) =>
      request(`/devices/${encodeId(acsDeviceId)}/operations/${encodeId(op)}:run`, {
        method: 'POST',
        base: opsBase,
        body,
        idempotencyKey: assertWriteKey(opts.idempotencyKey),
        parse: parseOperationRunAccepted,
      }),

    writeRawParameters: async (acsDeviceId: string, body: RawWriteRequest, opts: WriteOptions) =>
      request(`/devices/${encodeId(acsDeviceId)}/parameters/raw`, {
        method: 'PUT',
        base: opsBase,
        body,
        idempotencyKey: assertWriteKey(opts.idempotencyKey),
        parse: parseOperationWriteAccepted,
      }),

    listTemplates: async (opts?: ReadOptions) =>
      request('/templates', {
        method: 'GET',
        base: opsBase,
        parse: parseTemplateList,
        signal: opts?.signal,
      }),

    upsertTemplate: async (name, document, opts: WriteOptions) =>
      request(`/templates/${encodeURIComponent(name)}`, {
        method: 'PUT',
        base: opsBase,
        body: document,
        idempotencyKey: assertWriteKey(opts.idempotencyKey),
        parse: parsePresetWrite,
      }),

    listReconciliation: async (opts?: ReadOptions) =>
      request('/reconciliation', {
        method: 'GET',
        base: opsBase,
        parse: parseReconciliationList,
        signal: opts?.signal,
      }),

    getDeviceCapabilities: (_acsDeviceId: string, _options?: ReadOptions) =>
      Promise.reject(
        pendingEndpoint(
          'getDeviceCapabilities',
          'no door publishes a device capability list: `grep -n capabilities acs_service/routers/*.py` ' +
            'answers only the permission matrix (`identity.py`), the vendor-prefix registry ' +
            '(`capabilities.py`: `/vendor-registry`, `/devices/{id}/identity`) and the internal ' +
            '`device.get("capabilities")` read in `diagnostics.py:44`; `openapi.yaml` has no such path. ' +
            'The diagnostics page therefore offers speedtest and lets the API answer 422 action-unsupported',
        ),
      ),

    /* ---- human identity — `/ops/v1/sessions*`, `/ops/v1/permissions/effective` (R1, U-08-f) ---- */

    openSession: (session) =>
      request('/sessions', {
        method: 'POST',
        base: opsBase,
        anonymous: true,
        body: session,
        parse: parseSessionGranted,
      }),

    submitTotp: (factor) =>
      request('/sessions/totp', {
        method: 'POST',
        base: opsBase,
        anonymous: true,
        body: factor,
        parse: parseSessionGranted,
      }),

    closeSession: async (opts) =>
      request('/sessions/current', {
        method: 'DELETE',
        base: opsBase,
        idempotencyKey: assertWriteKey(opts.idempotencyKey),
        parse: parseSessionRevoked,
      }),

    effectivePermissions: () =>
      request('/permissions/effective', {
        method: 'GET',
        base: opsBase,
        parse: parseEffectivePermissions,
      }),

    /* ---- console users + ops settings — `routers/identity.py`, `routers/system.py` (U-16-g1) ---- */

    listUsers: (options?: ReadOptions) =>
      request('/users', {
        method: 'GET',
        signal: options?.signal,
        base: opsBase,
        parse: parseUserList,
      }),

    createUser: async (body, opts) =>
      request('/users', {
        method: 'POST',
        base: opsBase,
        body,
        idempotencyKey: assertWriteKey(opts.idempotencyKey),
        parse: parseUserMutationAccepted,
      }),

    patchUser: async (userId, body, opts) =>
      request(`/users/${encodeId(userId)}`, {
        method: 'PATCH',
        base: opsBase,
        body,
        idempotencyKey: assertWriteKey(opts.idempotencyKey),
        parse: parseUserMutationAccepted,
      }),

    deleteUser: async (userId, opts) =>
      request(`/users/${encodeId(userId)}`, {
        method: 'DELETE',
        base: opsBase,
        idempotencyKey: assertWriteKey(opts.idempotencyKey),
        parse: parseUserMutationAccepted,
      }),

    getOpsSettings: (options?: ReadOptions) =>
      request('/settings', {
        method: 'GET',
        signal: options?.signal,
        base: opsBase,
        parse: parseOpsSettingsRead,
      }),

    putOpsSettings: async (values, opts) =>
      request('/settings', {
        method: 'PUT',
        base: opsBase,
        // The door takes a BARE object, not a model: `system.py` says the whitelist is the whole
        // contract, which is why a permissive wrapper model would be a second, weaker contract.
        body: values,
        idempotencyKey: assertWriteKey(opts.idempotencyKey),
        parse: parseOpsSettingsRead,
      }),

    /* ---- permission matrix — `GET`/`PUT /ops/v1/permissions` (routers/identity.py, U-16-g2) ---- */

    getPermissionMatrix: (options?: ReadOptions) =>
      request('/permissions', {
        method: 'GET',
        signal: options?.signal,
        base: opsBase,
        parse: parsePermissionMatrix,
      }),

    putPermissionMatrix: async (body, opts) =>
      request('/permissions', {
        method: 'PUT',
        base: opsBase,
        body,
        // The door declares the header REQUIRED (`Header(...)` in `routers/identity.py`), so a missing
        // key is a 422 on the wire before any cell is judged; the sketch the façade stores carries the
        // CELLS, which is what makes a replay of the same body the same answer and a same-key
        // different-body retry the door's 409 `idempotency-conflict`.
        idempotencyKey: assertWriteKey(opts.idempotencyKey),
        parse: parseMatrixMutationAccepted,
      }),

    /* ---- system measurement — `components` · `queues` · `workers` (O4, DA-09) ----
       All three are READS on the `health.read` key, and all three are root-relative to the ops
       prefix (`/ops/v1/system/components`, `/ops/v1/system/queues`, `/ops/v1/system/workers`) —
       the same shape the settings and the matrix doors use.

       `signal` (DA-63) is forwarded on all three: one page opens three doors, and a page left
       for another screen has no business finishing three reads it no longer needs.

       `/metrics` is deliberately NOT here: it is the Prometheus text exposition, scraped by
       Prometheus. A browser cannot read a plain-text exposition as JSON, and a JSON-shaped
       reading of it would be a second contract. */

    getSystemComponents: (options?: ReadOptions) =>
      request('/system/components', {
        method: 'GET',
        signal: options?.signal,
        base: opsBase,
        parse: parseSystemComponents,
      }),

    getSystemQueues: (options?: ReadOptions) =>
      request('/system/queues', {
        method: 'GET',
        signal: options?.signal,
        base: opsBase,
        parse: parseSystemQueues,
      }),

    getSystemWorkers: (options?: ReadOptions) =>
      request('/system/workers', {
        method: 'GET',
        signal: options?.signal,
        base: opsBase,
        parse: parseSystemWorkers,
      }),

    /* ---- the subscriber surface (S1, G-SUB-01/03) ----
       Six doors on the `/ops/v1` root, and every one of them is a HUMAN door: the
       three capability keys (`subscriber.read` · `subscriber.manage` ·
       `service.profile`) carry no machine scope, so the bearer is the console's
       own session.

       THE MASK IS NOT THE CLIENT'S BUSINESS. `subscriber.read` at `read` gets the
       identifying fields masked by the DOOR; at `edit` it gets them whole. The
       client sends no `?full=` and no body flag, because the level on the key
       decides the fields and a request flag would be a second way to decide.

       `PUT /service-profiles/{profile_id}` sends NO `Idempotency-Key`: the door
       declares none (the body IS the row, so a retry writes the same row), and
       the house rule is replay-EQUIVALENCE, not «every write takes a key». The
       other two writes take one, and the key is required — a replay there would
       mint a second row for one person. */

    listSubscribers: (query: ListSubscribersQuery = {}) =>
      request('/subscribers', {
        method: 'GET',
        base: opsBase,
        query: { status: query.status, offset: query.offset, limit: query.limit },
        parse: parseSubscriberList,
      }),

    getSubscriber: (subscriberId: string) =>
      request(`/subscribers/${encodeId(subscriberId)}`, {
        method: 'GET',
        base: opsBase,
        parse: parseSubscriber,
      }),

    createSubscriber: async (body: SubscriberCreateRequest, opts: WriteOptions) =>
      request('/subscribers', {
        method: 'POST',
        base: opsBase,
        body,
        // REQUIRED by the door: the second call would mint a SECOND subscriber for one
        // person, so the key is what makes the retry answer the first call's answer.
        idempotencyKey: assertWriteKey(opts.idempotencyKey),
        parse: parseSubscriber,
      }),

    listServiceProfiles: (query: ListServiceProfilesQuery = {}) =>
      request('/service-profiles', {
        method: 'GET',
        base: opsBase,
        query: { offset: query.offset, limit: query.limit },
        parse: parseServiceProfileList,
      }),

    createServiceProfile: async (body: ServiceProfileWriteRequest, opts: WriteOptions) =>
      request('/service-profiles', {
        method: 'POST',
        base: opsBase,
        body,
        // REQUIRED by the door: a replay would mint a second profile under a second key,
        // and two profiles with one name are two rows an operator has to choose between.
        idempotencyKey: assertWriteKey(opts.idempotencyKey),
        parse: parseServiceProfile,
      }),

    putServiceProfile: async (profileId: string, body: ServiceProfileWriteRequest) =>
      request(`/service-profiles/${encodeId(profileId)}`, {
        method: 'PUT',
        base: opsBase,
        body,
        // NO `Idempotency-Key` — the door declares none, and a body that IS the whole
        // row makes the retry write the same row with the same values.
        parse: parseServiceProfile,
      }),

    /* ---- the subscriber search (S2) + the 360-degree view (S3) ----
       The search is a POST on the INTEGRATION prefix and it carries NO
       `Idempotency-Key`: the criteria travel in the body (a phone number in a
       query string is a phone number in an access log), and the house rule is
       replay-EQUIVALENCE, not «every write takes a key» — a search changes
       nothing in the caller's state, a replay of the same criteria yields the
       same page, and the published door declares no key parameter. So
       `assertWriteKey` is deliberately NOT applied here: it would make a
       key-less read fail as a contract error.

       The overview is a GET on the `/ops/v1` root, like the rest of the
       subscriber surface. */

    searchSubscribers: (body: SubscriberSearchRequest) =>
      request('/devices:search', {
        method: 'POST',
        body,
        parse: parseSubscriberSearchResponse,
      }),

    getSubscriberOverview: (subscriberId: string) =>
      request(`/subscribers/${encodeId(subscriberId)}/overview`, {
        method: 'GET',
        base: opsBase,
        parse: parseSubscriberOverview,
      }),

    /* ---- the subscriber's lifecycle (S4, G-SUB-05) ----
       Two doors on the `/ops/v1` root, and **NO BODY**: there is no field through
       which the caller could put personal data into the request, so the request
       carries the path, the header and nothing else. The client sends no body and no
       `Content-Type` — the door declares no `requestBody` and a body would be a
       second contract.

       The `Idempotency-Key` is REQUIRED on both, and `assertWriteKey` keeps the
       console on the required side of that line: a replay here is not equivalent
       without it, because the second call would meet a row that is already suspended
       and answer its own conflict instead of the stored answer. */

    suspendSubscriber: async (subscriberId: string, opts: WriteOptions) =>
      request(`/subscribers/${encodeId(subscriberId)}:suspend`, {
        method: 'POST',
        base: opsBase,
        idempotencyKey: assertWriteKey(opts.idempotencyKey),
        parse: parseSubscriber,
      }),

    resumeSubscriber: async (subscriberId: string, opts: WriteOptions) =>
      request(`/subscribers/${encodeId(subscriberId)}:resume`, {
        method: 'POST',
        base: opsBase,
        idempotencyKey: assertWriteKey(opts.idempotencyKey),
        parse: parseSubscriber,
      }),
  };
}

/* ------------------------------------------------------------------ */
/* helpers                                                            */
/* ------------------------------------------------------------------ */

/** The `/ops/v1` root that matches an integration-prefix `baseUrl`. */
export function opsBaseUrlFor(baseUrl: string): string {
  const trimmed = baseUrl.replace(/\/+$/, '');
  const OPS_SUFFIX = '/ops/v1';
  const INTEGRATION_SUFFIX = '/integration/v1';
  if (trimmed.endsWith(INTEGRATION_SUFFIX)) {
    return `${trimmed.slice(0, -INTEGRATION_SUFFIX.length)}${OPS_SUFFIX}`;
  }
  // A proxied prefix (e.g. `/api`): the ops surface hangs off the same root.
  return `${trimmed}${OPS_SUFFIX}`;
}

/** Device/job ids are opaque refs (`acs-device:…`, `job:…`) — encode, never guess. */
function encodeId(raw: string): string {
  if (raw.length === 0) {
    throw new ApiError('contract', 'path identifier must not be empty');
  }
  return encodeURIComponent(raw);
}

export function buildUrl(
  baseUrl: string,
  path: string,
  query?: Record<string, QueryValue>,
): string {
  const base = baseUrl.replace(/\/+$/, '');
  const origin = documentOrigin();
  const absolute = /^https?:\/\//i.test(base) ? `${base}${path}` : `${origin}${base}${path}`;
  const url = new URL(absolute);
  if (query) {
    for (const [key, value] of Object.entries(query)) {
      if (value === undefined || value === null || value === '') continue;
      url.searchParams.set(key, String(value));
    }
  }
  return url.toString();
}

function documentOrigin(): string {
  if (typeof window !== 'undefined' && window.location?.origin) {
    return window.location.origin;
  }
  throw new ApiError(
    'contract',
    'baseUrl must be absolute (no document origin available to resolve a relative one)',
  );
}

/** `online` serialises as `true`/`false`; empty filters are omitted entirely. */
function toQuery(
  query: ListDevicesQuery | ListParamsQuery | ListStationsQuery | ListEventsQuery,
): Record<string, QueryValue> {
  const out: Record<string, QueryValue> = {
    cursor: query.cursor,
    limit: query.limit,
  };
  if ('online' in query && typeof query.online === 'boolean') out.online = query.online;
  if ('manufacturer' in query) out.manufacturer = query.manufacturer;
  if ('model' in query) out.model = query.model;
  if ('path' in query) out.path = query.path;
  if ('source' in query) out.source = query.source;
  return out;
}
